import { Sparkles, MessageCircle, Calendar, MapPin } from "lucide-react";
import { BUSINESS, HERO_IMAGE } from "@/data/salonData";
import { openWhatsApp } from "@/lib/whatsapp";
import SparkleBackground from "./SparkleBackground";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Background gradients */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#f9d0da]/60 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-radial from-[#d4af37]/15 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_#fff5f8_0%,_#fffafc_60%)]" />
      </div>

      <SparkleBackground density={22} />

      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        {/* Left text */}
        <div className="lg:col-span-7 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-pink mb-7">
            <Sparkles size={14} className="text-[#d4af37]" />
            <span className="text-xs tracking-[0.25em] uppercase text-[#b56477] font-medium">
              Ujjain's Premium Beauty Studio
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-medium leading-[1.02] text-[#2a1b18]"
          >
            Luxury Is Not <br />
            <span className="italic font-light text-gradient-rose">
              Expensive,
            </span>{" "}
            <br />
            It's <span className="text-gradient-gold italic">Priceless</span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-[#5c4642] max-w-xl mx-auto lg:mx-0 leading-relaxed">
            {BUSINESS.subheadline}. Bridal makeovers, skin glow rituals,
            advanced hair care &amp; certified academy — designed for women
            who deserve nothing less.
          </p>

          {/* CTAs */}
          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
            <a
              href="#book"
              data-testid="hero-book-btn"
              className="btn-rose px-7 py-4 rounded-full text-sm md:text-base font-medium inline-flex items-center justify-center gap-2"
            >
              <Calendar size={18} />
              Book Appointment
            </a>
            <button
              data-testid="hero-whatsapp-btn"
              onClick={() =>
                openWhatsApp(
                  "Hello Anshi's Makeover! I'd love to know more about your services."
                )
              }
              className="btn-outline-rose px-7 py-4 rounded-full text-sm md:text-base font-medium inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={18} />
              WhatsApp Now
            </button>
          </div>

          {/* Mini stats */}
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
            {[
              { num: "500+", label: "Happy Brides" },
              { num: "60+", label: "Beauty Services" },
              { num: "5★", label: "Studio Rating" },
            ].map((s) => (
              <div
                key={s.label}
                className="text-center lg:text-left"
              >
                <div className="font-display text-3xl md:text-4xl text-[#b56477] font-medium">
                  {s.num}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-[#5c4642]/70 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative">
          <div className="relative animate-float-slow">
            <div className="hero-frame aspect-[4/5] shadow-[0_30px_80px_-20px_rgba(217,138,153,0.4)]">
              <img
                src={HERO_IMAGE}
                alt="Anshi's Makeover bridal portfolio"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Floating tag — location */}
            <div className="absolute -bottom-6 -left-3 sm:-left-8 glass rounded-2xl px-4 py-3 flex items-center gap-3 max-w-[260px] shadow-xl">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f9d0da] to-[#d98a99] flex items-center justify-center flex-shrink-0">
                <MapPin size={16} color="#fff" />
              </div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider text-[#b56477] font-semibold">
                  Find us
                </div>
                <div className="text-xs text-[#2a1b18] leading-snug">
                  Freeganj, Ujjain (M.P.)
                </div>
              </div>
            </div>

            {/* Floating tag — gold badge */}
            <div className="absolute -top-4 -right-3 sm:-right-6 bg-white rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl glow-gold">
              <Sparkles size={18} className="text-[#d4af37]" />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider text-[#d4af37] font-semibold">
                  Certified
                </div>
                <div className="text-xs text-[#2a1b18]">Atul Chauhan Masterclass</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
